/* eslint-disable no-console */
import React, { PropsWithChildren } from 'react';
import TranslatedText from 'components/i18n/TranslatedText';
import { GameType, IBlockRegion, IFortniteLeaderboardBlockData, LeaderboardType } from '../utils';
import { Section, SectionTitle } from 'components/admin-bridge/EditorModal/ModalComponents/Section';
import {
  UploadZoneWrapper,
  DesktopBackgroundUploadZone,
  MobileBackgroundUploadZone,
  StyledLabel,
  OptionToggle,
  OptionsToggle,
  BlockPanelSettingsWrapper,
  BlockPanelSettingsTitle,
} from './styles';
import { Checkbox } from 'components/admin2/ui/Checkbox';
import PageBlockSettings from 'components/admin-bridge/PageBlockSettings';
import { useAdminTranslation } from 'hooks/use-translation';
import usePageBlock from 'hooks/use-page-block';
import { StyledDropdown } from '../styles';

interface IBlockSettingsProps {
  isOpen?: boolean;
  item: IFortniteLeaderboardBlockData;
  onChange: (item: IFortniteLeaderboardBlockData) => void;
  onClose?: () => void;
  region: IBlockRegion;
}

type EditableStringPaths = (
  'defaultFilters.gameType' |
  'defaultFilters.leaderboardType' |
  'background.image.desktop' |
  'background.image.mobile' |
  'title.show' |
  'description.show'
);

type WrapperProps = PropsWithChildren & { isOpen: boolean; onClose: () => void; region: IBlockRegion; };

const Wrapper: React.FC<WrapperProps> = ({ region, children, isOpen, onClose }) => {
  const { t } = useAdminTranslation();

  if (region === 'panel') {
    return (
      <BlockPanelSettingsWrapper>
        <BlockPanelSettingsTitle>{t('ADMIN_LABEL_FORTNITE_LEADERBOARD_SETTINGS')}</BlockPanelSettingsTitle>
        {children}
      </BlockPanelSettingsWrapper>
    );
  }

  return (
    <PageBlockSettings
      isOpen={isOpen}
      titleKey="ADMIN_LABEL_FORTNITE_LEADERBOARD_SETTINGS"
      onClose={onClose}
    >
      {children}
    </PageBlockSettings>
  );
};

// tslint:disable-next-line: no-empty
const NO_OP = () => {};

const BlockSettings: React.FC<IBlockSettingsProps> = ({ item, onChange, isOpen = false, onClose, region }) => {
  const { t } = useAdminTranslation();
  const { handleChange } = usePageBlock<EditableStringPaths, IFortniteLeaderboardBlockData>({
    item,
    onChange,
  });

  const handleClearDesktopBackground = React.useCallback(() => {
    handleChange('background.image.desktop')('');
  }, [handleChange]);

  const handleClearMobileBackground = React.useCallback(() => {
    handleChange('background.image.mobile')('');
  }, [handleChange]);

  const changeGameType = React.useCallback((gameType: GameType) => () => {
    handleChange('defaultFilters.gameType')(gameType);
  }, [handleChange]);

  const changeLeaderboardType = (leaderboardType: LeaderboardType) => () => {
    handleChange('defaultFilters.leaderboardType')(leaderboardType);
  };

  const handleClose = React.useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  return (
    <Wrapper
      isOpen={isOpen}
      onClose={handleClose}
      region={region}
    >
      <Section gap={30}>
        {/* Display */}
        <Section gap={16}>
          <TranslatedText component={SectionTitle} stringKey="ADMIN_LABEL_DISPLAY" />
          <Section gap={15}>
            <Checkbox
              checked={item.title.show}
              labelKey="ADMIN_LABEL_TITLE"
              onCheckedChange={handleChange('title.show')}
              testId="fortniteLeaderboardTitleCheckbox"
            />
            <Checkbox
              checked={item.description.show}
              labelKey="ADMIN_LABEL_DESCRIPTION"
              onCheckedChange={handleChange('description.show')}
              testId="fortniteLeaderboardTDescriptionCheckbox"
            />
          </Section>
        </Section>

        {/* Default Data */}
        <Section gap={16}>
          <TranslatedText component={SectionTitle} stringKey="DEFAULT_DATA" />
          <OptionsToggle>
            <OptionToggle
              onClick={changeLeaderboardType('general')}
              active={item.defaultFilters.leaderboardType === 'general'}
            >
              {t('GENERAL')}
            </OptionToggle>
            <OptionToggle
              onClick={changeLeaderboardType('ranked')}
              active={item.defaultFilters.leaderboardType === 'ranked'}
            >
              {t('RANKED')}
            </OptionToggle>
          </OptionsToggle>

          <OptionsToggle>
            <OptionToggle
              onClick={changeGameType('battle-royale')}
              active={item.defaultFilters.gameType === 'battle-royale'}
            >
              {t('BATTLE_ROYALE')}
            </OptionToggle>
            <OptionToggle
              onClick={changeGameType('zero-build')}
              active={item.defaultFilters.gameType === 'zero-build'}
            >
              {t('ZERO_BUILD')}
            </OptionToggle>
          </OptionsToggle>

          <Section gap={6}>
            <StyledDropdown
              labelKey="SEASON"
              isAdmin={true}
              padding="0"
              onChange={NO_OP}
              options={[
                {
                  label: 'Season 1',
                  value: 'season1',
                },
              ]}
            />
            <StyledDropdown
              labelKey="TOURNAMENT"
              isAdmin={true}
              padding="0"
              onChange={NO_OP}
              options={[
                {
                  label: 'Tournament 1',
                  value: 'tournament1',
                },
              ]}
            />
            <StyledDropdown
              labelKey="SESSION"
              isAdmin={true}
              padding="0"
              onChange={NO_OP}
              options={[
                {
                  label: 'Session 1',
                  value: 'session1',
                },
              ]}
            />
          </Section>
        </Section>

        {/* Background images */}
        <Section gap={20}>
          <UploadZoneWrapper>
            <StyledLabel padding="0" labelKey="ADMIN_LABEL_DESKTOP" />
            <DesktopBackgroundUploadZone
              imagePreview={item.background.image.desktop}
              onClearImage={handleClearDesktopBackground}
              onFileSubmit={handleChange('background.image.desktop')}
            />
          </UploadZoneWrapper>
          <UploadZoneWrapper>
            <StyledLabel padding="0" labelKey="ADMIN_LABEL_MOBILE" />
            <MobileBackgroundUploadZone
              imagePreview={item.background.image.mobile}
              onClearImage={handleClearMobileBackground}
              onFileSubmit={handleChange('background.image.mobile')}
            />
          </UploadZoneWrapper>
        </Section>
      </Section>
    </Wrapper>
  );
};

export default BlockSettings;
